import React from 'react';

// Awarded-brief celebration artwork (awarded-brief-state brief §6). One mark,
// distinct renders per size — a single asset scaled down turns to mush below
// ~32px, so the small "row" variant is a simplified drawing, not the full one.
//
// Palette: brand green #5CB82B, gold #F0B429, blue #1F6C87, all outlined in
// #3B6D11. Flat, no gradients — consistent with the recognition ladders.
//
// Variants:
//   'full' — 82px hero (three ribbons + confetti). Never below 48px (§6).
//   'row'  — ~19px art for the What's-new tile (one ribbon, two confetti).
// The dashboard-alert background is a separate wide layer (StreamersBackground).

const OUTLINE = '#3B6D11';
const GREEN = '#5CB82B';
const GOLD = '#F0B429';
const BLUE = '#1F6C87';

export function StreamersMark({ variant = 'full', size, className = '', title }) {
  if (variant === 'row') {
    const s = size || 19;
    const d = 'M8 22 C 5.5 16, 10 13, 7 7 C 5.5 4, 9 3.5, 8 2';
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" className={className} role={title ? 'img' : undefined} aria-hidden={title ? undefined : true} aria-label={title}>
        {/* one curled ribbon (outline stroke + colour on top) */}
        <path d={d} fill="none" stroke={OUTLINE} strokeWidth="3.4" strokeLinecap="round" />
        <path d={d} fill="none" stroke={GREEN} strokeWidth="1.8" strokeLinecap="round" />
        {/* two confetti */}
        <circle cx="18" cy="6" r="2.1" fill={GOLD} stroke={OUTLINE} strokeWidth="1" />
        <rect x="15" y="14" width="3.6" height="3.6" rx="0.6" transform="rotate(20 16.8 15.8)" fill={BLUE} stroke={OUTLINE} strokeWidth="1" />
      </svg>
    );
  }
  // full hero — curled party streamers fanning up + scattered confetti. Each
  // streamer is a colour stroke over a slightly wider dark stroke, so it reads
  // as an outlined ribbon (§6 palette) without needing a filled twist shape.
  const s = size || 82;
  const STREAMERS = [
    { d: 'M28 86 C 20 66, 33 58, 24 42 C 17 30, 28 23, 22 12', c: GREEN, w: 4.4 },
    { d: 'M45 88 C 41 64, 50 56, 45 37 C 41 23, 49 17, 45 7',  c: GOLD,  w: 4.4 },
    { d: 'M62 86 C 67 66, 57 58, 67 42 C 74 30, 65 22, 71 11', c: BLUE,  w: 4.4 },
    { d: 'M52 90 C 59 72, 51 62, 61 49',                        c: GREEN, w: 3.6 },
  ];
  const CONFETTI = [
    { t: 'rect', x: 12, y: 24, s: 8, r: 20, c: GOLD },
    { t: 'circ', x: 80, y: 28, r: 4.6, c: GREEN },
    { t: 'rect', x: 74, y: 54, s: 7, r: -16, c: BLUE },
    { t: 'circ', x: 16, y: 54, r: 3.6, c: BLUE },
    { t: 'tri',  x: 84, y: 44, c: GOLD },
    { t: 'circ', x: 33, y: 10, r: 3, c: GOLD },
    { t: 'rect', x: 58, y: 6, s: 6, r: 26, c: GREEN },
  ];
  return (
    <svg width={s} height={s} viewBox="0 0 96 96" className={className} role="img" aria-hidden={title ? undefined : true} aria-label={title}>
      {STREAMERS.map((r, i) => (
        <g key={i}>
          <path d={r.d} fill="none" stroke={OUTLINE} strokeWidth={r.w + 2.6} strokeLinecap="round" />
          <path d={r.d} fill="none" stroke={r.c} strokeWidth={r.w} strokeLinecap="round" />
        </g>
      ))}
      {CONFETTI.map((p, i) => {
        if (p.t === 'circ') return <circle key={i} cx={p.x} cy={p.y} r={p.r} fill={p.c} stroke={OUTLINE} strokeWidth="1.4" />;
        if (p.t === 'tri') return <path key={i} d={`M${p.x} ${p.y} l4.5 8 l-9 0 z`} fill={p.c} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />;
        return <rect key={i} x={p.x} y={p.y} width={p.s} height={p.s} rx="1.4" transform={`rotate(${p.r} ${p.x + p.s / 2} ${p.y + p.s / 2})`} fill={p.c} stroke={OUTLINE} strokeWidth="1.4" />;
      })}
    </svg>
  );
}

// Decorative ribbon layer for the dashboard alert (§5) — ribbons hanging from
// the top edge, drifting down across the full width, behind the content. The
// caller positions it absolutely; it's aria-hidden + pointer-events:none and
// the alert must read correctly with it absent.
export function StreamersBackground({ className = '' }) {
  return (
    <svg
      className={className}
      aria-hidden="true"
      preserveAspectRatio="none"
      viewBox="0 0 400 80"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55, pointerEvents: 'none' }}
    >
      {[10, 70, 140, 210, 280, 340, 390].map((x, i) => {
        const c = [GREEN, GOLD, BLUE][i % 3];
        return (
          <path key={x} d={`M${x} 0 C ${x - 6} 26, ${x + 6} 46, ${x - 3} 78`} fill="none" stroke={c} strokeWidth="4" strokeLinecap="round" />
        );
      })}
    </svg>
  );
}

export default StreamersMark;
